import React, { useContext, useMemo, useState } from "react";
import { Menu, MenuItem } from "@foris/avocado-suite";
import { ContextApp } from "@config/Context/contextApp";
import { useTranslation } from "react-i18next";
import TourTooltipBeacon from "../tour-tooltip-beacon";
import styles from "./header.module.scss";
import OnboardingVideoModal from "../onboarding-video-modal";
import {
  DELETE_SECTIONS_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";

export interface IMenuProps {
  hasSessions?: boolean;
  openEdit?: (value: any) => void;
  openDisabledSession?: (value: any) => void;
  openChangeComponent?: (value: any) => void;
  onDisabledSessionError?: () => void;
}

const SectionMenu: React.FC<IMenuProps> = (props: IMenuProps) => {
  const [showSectionMenuTour, setShowSectionMenuTour] = useState(false);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);
  const {
    openEdit,
    openDisabledSession,
    openChangeComponent,
    onDisabledSessionError,
    hasSessions,
  } = props;
  const { user } = useContext(ContextApp);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const userCanCreateOrEditSessions =
    user?.permissions?.create &&
    user?.abilities?.can_edit_links_and_sections &&
    user?.abilities?.can_edit_or_delete_sessions;

  const showTourHotspot = useMemo(() => {
    // temporarily disable onboarding
    const isDisabled = true;
    return shouldShowHotspot(DELETE_SECTIONS_ONBOARDING) && !isDisabled;
  }, [onboardingsSeen]);

  const handleDeleteSession = (val: any) => {
    if (!user?.abilities?.can_edit_or_delete_sessions && hasSessions) {
      onDisabledSessionError?.();
      return;
    }

    openDisabledSession?.(val);
  };

  const menuItems = useMemo(() => {
    const items: MenuItem[] = [
      {
        label: t("dimension.section.header.menu.change-component"),
        icon: "edit",
        onClick: openChangeComponent,
      },
    ];

    if (!!user?.abilities?.can_delete_sections) {
      items.push({
        label: (
          <div className={styles.sectionMenuTour}>
            {t("dimension.section.header.menu.delete-section")}

            {showTourHotspot && (
              <button
                className={styles.sectionMenuTour_beaconButton}
                onClick={e => {
                  e.stopPropagation();
                  setShowSectionMenuTour(true);
                  setIsOnboardingOpen(true, "delete-sections");
                }}
              >
                <TourTooltipBeacon />
              </button>
            )}
          </div>
        ),
        icon: "trash",
        onClick: handleDeleteSession,
      });
    }

    if (userCanCreateOrEditSessions) {
      items.unshift({
        label: t("dimension.section.header.menu.create-session"),
        icon: "plus",
        onClick: openEdit,
      });
    }

    return items;
  }, [userCanCreateOrEditSessions, language, showTourHotspot]);

  return (
    <>
      {showTourHotspot && (
        <OnboardingVideoModal
          isOpen={showSectionMenuTour}
          title={t("onboarding.delete-sections.title")}
          description={t("onboarding.delete-sections.description")}
          videoUrl="https://files.foris.ai/darwin/demo_foris_agents_edutic_santiago_2025.mov"
          onClose={() => {
            setShowSectionMenuTour(false);
            setIsOnboardingOpen(false, "delete-sections");
          }}
        />
      )}

      <Menu listOrigin="right" items={menuItems} />
    </>
  );
};

export default SectionMenu;
